import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Badge, Card, CardBody, Col, Input, InputGroup, Row } from 'reactstrap';
import { GuardInPages } from 'guards/Guard';
import BienvenidoComponent from 'pages/components/Bienvenido';
import LoadingComponent from 'pages/components/Loading';
import { getMenu } from 'services/MenuService';
import { GetAuthUser } from 'services/AuthService';
import { getEmpleadoById } from 'services/EmpleadosService';
import './home.css';

const moduleDescriptions = {
  Agenda: 'Gestiona eventos, tareas y coordinaciones diarias.',
  Almacenes: 'Consulta depositos, stock asociado y organizacion de materiales.',
  'Auditar Deposito': 'Controla movimientos y validaciones del deposito.',
  'Cambios de Equipos': 'Administra recambios, entregas y seguimiento tecnico.',
  Caes: 'Revisa comprobantes, CAE y documentos asociados.',
  Casos: 'Hace seguimiento de incidentes, tickets y novedades.',
  'Categorias Equipos': 'Ordena y mantiene categorias del inventario tecnico.',
  Dashboard: 'Accede a metricas generales y vistas de control.',
  DatosClientes: 'Consulta informacion consolidada y datos auxiliares de clientes.',
  Clientes: 'Gestiona altas, estados y seguimiento comercial.',
  'Control Pagos': 'Supervisa cobros, estados y conciliaciones.',
  Egresos: 'Registra y consulta salidas de dinero del sistema.',
  'Equipos General': 'Administra el inventario general de equipos.',
  Empleados: 'Consulta legajos, roles y operaciones del personal.',
  'Facturacion Automatica': 'Controla programaciones y procesos de facturacion.',
  Historico: 'Revisa historial y trazabilidad operativa.',
  'Lista de Espera': 'Visualiza pendientes y orden de atencion.',
  Obras: 'Gestiona obras activas y recursos asociados.',
  'Retiros De Servicios': 'Coordina bajas y retiros operativos.',
  Servicios: 'Administra instalaciones, configuraciones y estado de servicios.',
  'Stock Multiple': 'Consulta materiales y stock disponible.',
  Vehiculos: 'Gestiona unidades, estado y documentacion vehicular.',
  ReportesFacturacion: 'Analiza reportes y diferencias de facturacion.',
  'Reportes Facturacion': 'Analiza reportes y diferencias de facturacion.',
  'Clientes Fiscales': 'Consulta clientes con configuracion fiscal.',
  Zonas: 'Administra cobertura, sectores y areas operativas.',
  'Equipos Excel': 'Importa y revisa cargas masivas de equipos.',
  'Instaladores Bot': 'Opera herramientas y automatizaciones del bot.',
  Precios: 'Consulta y administra listas o referencias de precios.',
  'PuntosDeVenta': 'Gestiona puntos de venta habilitados.',
  PuntosDeVenta: 'Gestiona puntos de venta habilitados.',
};

const normalizeMenuItems = (menu) =>
  menu
    .filter((item) => item?.visible && item?.layout && item?.path)
    .map((item) => ({
      title: item.name,
      description: moduleDescriptions[item.name] || 'Accede al modulo habilitado para esta cuenta.',
      icon: item.icon || 'fas fa-th-large',
      path: `${item.layout}${item.path}`,
    }));

export default function Home() {
  const history = useHistory();
  const [bienvenidoVisible, setBienvenidoVisible] = useState(false);
  const [loadingVisible, setLoadingVisible] = useState(true);
  const [allowedQuickLinks, setAllowedQuickLinks] = useState([]);
  const [search, setSearch] = useState('');
  const [displayName, setDisplayName] = useState(localStorage.getItem('authDisplayName') || '');

  useEffect(() => {
    GuardInPages(history);
    const shouldShowWelcome = sessionStorage.getItem('showHomeWelcome') === '1';
    if (!shouldShowWelcome) {
      return undefined;
    }

    sessionStorage.removeItem('showHomeWelcome');
    setBienvenidoVisible(true);
    const timer = setTimeout(() => setBienvenidoVisible(false), 2000);
    return () => clearTimeout(timer);
  }, [history]);

  useEffect(() => {
    const loadHomeData = async () => {
      setLoadingVisible(true);

      const menu = await getMenu();
      if (!Array.isArray(menu)) {
        setAllowedQuickLinks([]);
      } else {
        setAllowedQuickLinks(normalizeMenuItems(menu));
      }

      const cachedName = localStorage.getItem('authDisplayName');
      if (cachedName) {
        setDisplayName(cachedName);
      }

      const authUser = await GetAuthUser();
      if (!authUser?.usuario) {
        setLoadingVisible(false);
        return;
      }

      let nextName = authUser.usuario.usuario || cachedName || '';

      if (authUser.usuario.empleadoId) {
        const empleado = await getEmpleadoById(authUser.usuario.empleadoId);
        if (empleado?.nombre || empleado?.apellido) {
          nextName = `${empleado.nombre || ''} ${empleado.apellido || ''}`.trim();
        }
      }

      if (nextName) {
        localStorage.setItem('authDisplayName', nextName);
        setDisplayName(nextName);
      }

      setLoadingVisible(false);
    };

    loadHomeData();
  }, []);

  const goTo = (path) => history.push(path);
  const filteredQuickLinks = allowedQuickLinks.filter((item) =>
    item.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <LoadingComponent Visible={loadingVisible && !bienvenidoVisible} />
      <BienvenidoComponent Visible={bienvenidoVisible} nombre={displayName} />
      <div className="home-page">
        <section className="home-hero">
          <div className="home-hero__glow home-hero__glow--left" />
          <div className="home-hero__glow home-hero__glow--right" />
          <Badge className="home-badge">Panel operativo</Badge>
          <h1 className="home-title">Accesos rapidos</h1>
          <p className="home-subtitle">
            Esta portada muestra solo modulos habilitados para el usuario actual.
          </p>
          <div className="home-search">
            <InputGroup>
              <Input
                placeholder="Buscar modulo"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="home-search__input"
              />
            </InputGroup>
          </div>
          <div className="home-kpi">
            <span className="home-kpi__value">{filteredQuickLinks.length}</span>
            <span className="home-kpi__label">modulos visibles</span>
          </div>
          <Row>
            {filteredQuickLinks.map((item) => (
              <Col xl="4" md="6" key={item.title} className="mb-4">
                <Card className="home-link-card h-100" onClick={() => goTo(item.path)}>
                  <CardBody>
                    <div className="home-link-card__icon">
                      <i className={item.icon} />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <span className="home-link-card__cta">Abrir modulo</span>
                  </CardBody>
                </Card>
              </Col>
            ))}
            {!filteredQuickLinks.length ? (
              <Col>
                <Card className="home-empty-card">
                  <CardBody>
                    <h3>{allowedQuickLinks.length ? 'Sin resultados' : 'Sin accesos rapidos disponibles'}</h3>
                    <p>
                      {allowedQuickLinks.length
                        ? 'No hay modulos que coincidan con la busqueda actual.'
                        : 'Esta cuenta no tiene modulos destacados configurados para la portada. Usa el menu lateral para entrar a las secciones permitidas.'}
                    </p>
                  </CardBody>
                </Card>
              </Col>
            ) : null}
          </Row>
        </section>
      </div>
    </>
  );
}
